import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';

type RevealVariant = 'up' | 'fade' | 'left' | 'right' | 'zoom' | 'mask';

interface RevealProps {
  children: ReactNode;
  variant?: RevealVariant;
  delay?: number;
  duration?: number;
  className?: string;
  threshold?: number;
}

const hiddenStyles: Record<RevealVariant, CSSProperties> = {
  up: { opacity: 0, transform: 'translate3d(0, 28px, 0)' },
  fade: { opacity: 0 },
  left: { opacity: 0, transform: 'translate3d(-36px, 0, 0)' },
  right: { opacity: 0, transform: 'translate3d(36px, 0, 0)' },
  zoom: { opacity: 0, transform: 'scale(1.07)' },
  mask: { clipPath: 'inset(0 0 100% 0)' },
};

const visibleStyles: Record<RevealVariant, CSSProperties> = {
  up: { opacity: 1, transform: 'translate3d(0, 0, 0)' },
  fade: { opacity: 1 },
  left: { opacity: 1, transform: 'translate3d(0, 0, 0)' },
  right: { opacity: 1, transform: 'translate3d(0, 0, 0)' },
  zoom: { opacity: 1, transform: 'scale(1)' },
  mask: { clipPath: 'inset(0 0 0% 0)' },
};

export default function Reveal({
  children,
  variant = 'up',
  delay = 0,
  duration = 1000,
  className = '',
  threshold = 0.06,
}: RevealProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold, rootMargin: '0px 0px -4% 0px' },
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [threshold]);

  const style: CSSProperties = {
    ...(visible ? visibleStyles[variant] : hiddenStyles[variant]),
    transitionProperty: 'opacity, transform, clip-path',
    transitionDuration: `${duration}ms`,
    transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
    transitionDelay: `${delay}ms`,
    willChange: 'opacity, transform',
  };

  return (
    <div ref={ref} className={className} style={style}>
      {children}
    </div>
  );
}